import React from "react";
import "./CanvasTour.scss";
import RunOnceTour from "../../../tours/RunOnceTour";
import {
    Joyride, Props as JoyrideProps, Step, Options, Styles, Locale, ButtonType, Events,
    EventData, Controls, EventHandler, FloatingOptions, PartialDeep
} from "react-joyride";
import * as colors from "../../../shared/colors";
import { QerentComponent } from "../../../shared/components/DocumentedComponent";
import _ from "lodash";
import { useTooltipContainer } from "../../../shared/providers/TooltipContainerProvider";

// The pre-v3 `react-joyride` prop surface is the public template API, so it is modelled explicitly
// here and translated to the v3 shapes below...

// v2 theming keys that lived under `styles.options` (`spotlightShadow` has no v3 home and is dropped)...
type LegacyStyleOptions = Partial<Pick<Options, "arrowColor" | "backgroundColor" | "beaconSize" | "overlayColor" | "primaryColor" | "textColor" | "width" | "zIndex">> & {
    spotlightShadow?: string;
};

type LegacyStyles = PartialDeep<Styles> & {
    options?: LegacyStyleOptions;
    buttonNext?: React.CSSProperties;
    spotlightLegacy?: React.CSSProperties;
    overlayLegacy?: React.CSSProperties;
    overlayLegacyCenter?: React.CSSProperties;
};

type LegacyLocale = Locale & {
    nextLabelWithProgress?: React.ReactNode;
};

type LegacyFloaterProps = Partial<FloatingOptions> & {
    disableAnimation?: boolean;
};

// The v2 props whose v3 equivalents live in the `options` object; valid at both tour and step level...
type LegacyOptionProps = {
    showProgress?: boolean;
    spotlightPadding?: number;
    disableScrolling?: boolean;
    disableOverlay?: boolean;
    disableOverlayClose?: boolean;
    disableCloseOnEsc?: boolean;
    spotlightClicks?: boolean;
    scrollDuration?: number;
    scrollOffset?: number;
    offset?: number;
    showSkipButton?: boolean;
    hideBackButton?: boolean;
    hideCloseButton?: boolean;
    hideFooter?: boolean;
};

type LegacyStep = Omit<Step, "styles" | "locale"> & LegacyOptionProps & {
    disableBeacon?: boolean;
    event?: Options["beaconTrigger"];
    styles?: LegacyStyles;
    locale?: LegacyLocale;
    floaterProps?: LegacyFloaterProps;
};

type LegacyTourProps = Omit<Partial<JoyrideProps>, "steps" | "styles" | "locale"> & LegacyOptionProps & {
    callback?: EventHandler;
    getHelpers?: unknown;
    disableScrollParentFix?: boolean;
    disableBeacon?: boolean;
    steps?: LegacyStep[];
    styles?: LegacyStyles;
    locale?: LegacyLocale;
    floaterProps?: LegacyFloaterProps;
};

// The component's own defaults stay in the legacy shape and run through the same translation as
// template-supplied props...
const defaultProps: LegacyTourProps = {
    continuous: true,
    showSkipButton: true,
    spotlightPadding: 0,
    disableScrolling: true,
    spotlightClicks: false,
    locale: {
        back: 'Back',
        close: 'Close',
        last: 'Close',
        next: 'Next',
        skip: 'Skip Tour'
    },
    floaterProps: {
        disableAnimation: true
    },
    showProgress: true,
    styles: {
        options: {
            zIndex: 1400,
            primaryColor: colors.blue_1,
            arrowColor: "var(--colors-grey-8)",
            overlayColor: "color-mix(in srgb, var(--colors-grey-1) 60%, transparent)"
        },
        tooltip: {
            background: "var(--colors-grey-8)",
            color: "var(--colors-grey-2)"
        },
        buttonClose: {
            color: "var(--colors-grey-1)"
        },
        buttonSkip: {
            color: "var(--colors-grey-5)"
        }
    }
};

// Event types that existed before v3 - newer event types (hook and scroll events) are hidden from
// legacy `callback` handlers so their step-index bookkeeping is not thrown off by extra calls...
const legacyEventTypes = new Set<Events>(["tour:start", "step:before", "beacon", "tooltip", "step:after", "tour:end", "tour:status", "error:target_not_found"]);

const hasButtonOverrides = (props: LegacyOptionProps): boolean =>
    [props.showSkipButton, props.hideBackButton, props.hideCloseButton, props.hideFooter].some(value => value !== undefined);

const translateButtons = (props: LegacyOptionProps): ButtonType[] => props.hideFooter ? [] : [
    ...(props.hideBackButton ? [] : ["back" as const]),
    ...(props.hideCloseButton ? [] : ["close" as const]),
    "primary" as const,
    ...(props.showSkipButton ? ["skip" as const] : [])
];

// Legacy props whose v3 equivalent lives in the `options` object.  Only props that were actually
// supplied are mapped, so per-step values don't override the tour-level configuration...
const translateOptionProps = (props: LegacyOptionProps): Partial<Options> => ({
    ...(props.showProgress !== undefined && { showProgress: props.showProgress }),
    ...(props.spotlightPadding !== undefined && { spotlightPadding: props.spotlightPadding }),
    ...(props.disableScrolling !== undefined && { skipScroll: props.disableScrolling }),
    ...(props.disableOverlay !== undefined && { hideOverlay: props.disableOverlay }),
    ...(props.disableOverlayClose && { overlayClickAction: false as const }),
    ...(props.disableCloseOnEsc && { dismissKeyAction: false as const }),
    // The legacy default blocked interaction with the spotlit element; v3's default allows it...
    ...(props.spotlightClicks !== undefined && { blockTargetInteraction: !props.spotlightClicks }),
    ...(props.scrollDuration !== undefined && { scrollDuration: props.scrollDuration }),
    ...(props.scrollOffset !== undefined && { scrollOffset: props.scrollOffset }),
    ...(props.offset !== undefined && { offset: props.offset }),
    ...(hasButtonOverrides(props) && { buttons: translateButtons(props) })
});

// Theming values moved from `styles.options` into the top-level `options` prop in v3...
const translateStyles = (styles?: LegacyStyles): { options: Partial<Options>; styles: PartialDeep<Styles> } => {
    const { options = {}, buttonNext, spotlightLegacy, overlayLegacy, overlayLegacyCenter, ...rest } = styles ?? {};
    const { spotlightShadow, ...optionValues } = options;

    return {
        options: optionValues,
        styles: { ...rest, ...(buttonNext && { buttonPrimary: buttonNext }) }
    };
};

const translateLocale = ({ nextLabelWithProgress, ...rest }: LegacyLocale): Locale => ({
    ...rest,
    ...(nextLabelWithProgress !== undefined && {
        nextWithProgress: _.isString(nextLabelWithProgress)
            ? nextLabelWithProgress.replaceAll("{step}", "{current}").replaceAll("{steps}", "{total}")
            : nextLabelWithProgress
    })
});

// `react-floater` was replaced by floating-ui in v3, so only the animation toggle has a
// meaningful translation - the remaining floater options are dropped...
const translateFloaterProps = (styles: PartialDeep<Styles>, floaterProps?: LegacyFloaterProps): PartialDeep<Styles> =>
    floaterProps?.disableAnimation ? _.merge({}, styles, { floater: { transition: "none" } }) : styles;

const translateStep = (step: LegacyStep): Step => {
    const {
        disableBeacon, event, hideFooter, hideBackButton, hideCloseButton, showSkipButton,
        spotlightClicks, disableScrolling, disableOverlay, disableOverlayClose, disableCloseOnEsc,
        spotlightPadding, showProgress, scrollDuration, scrollOffset, offset,
        floaterProps, styles, locale, ...rest
    } = step;
    const translatedStyles = translateStyles(styles);

    // Option overrides sit directly on the step object in v3 (no `options` sub-object)...
    return {
        ...rest,
        ...translateOptionProps(step),
        ...translatedStyles.options,
        ...(disableBeacon !== undefined && { skipBeacon: disableBeacon }),
        ...(event !== undefined && { beaconTrigger: event }),
        ...(styles || floaterProps ? { styles: translateFloaterProps(translatedStyles.styles, floaterProps) } : {}),
        ...(locale && { locale: translateLocale(locale) })
    };
};

/**
 * Translates the pre-v3 `react-joyride` prop surface (which existing canvas templates were written
 * against) into the v3 API.  Props already in v3 shape (`options`, `onEvent`) pass through and take
 * precedence over the translated values.
 */
export const translateTourProps = (props: LegacyTourProps): JoyrideProps => {
    const {
        callback, onEvent, getHelpers,
        showProgress, spotlightPadding, disableScrolling, disableScrollParentFix,
        disableOverlay, disableOverlayClose, disableCloseOnEsc, spotlightClicks,
        showSkipButton, hideBackButton, hideCloseButton, hideFooter, disableBeacon,
        scrollDuration, scrollOffset, offset,
        floaterProps, locale, styles, options,
        steps, ...rest
    } = props;

    getHelpers && console.warn("CanvasTour: `getHelpers` is no longer supported - use the `controls` argument of the `onEvent`/`callback` prop instead.");

    const translatedStyles = translateStyles(styles);

    return {
        ...rest,
        steps: steps?.map(translateStep),
        ...(locale && { locale: translateLocale(locale) }),
        styles: translateFloaterProps(translatedStyles.styles, floaterProps),
        options: { ...translateOptionProps(props), ...translatedStyles.options, ...options },
        ...(callback || onEvent ? {
            onEvent: (data: EventData, controls: Controls) => {
                onEvent && onEvent(data, controls);
                callback && legacyEventTypes.has(data.type) && callback(data, controls);
            }
        } : {})
    };
};

export type CanvasTourProps = LegacyTourProps & {
    autoRun?: boolean;
    runOnce?: boolean;
    id?: string;
    autoRunDelay?: number;
};

const CanvasTour: QerentComponent<CanvasTourProps> = (props) => {
    const { autoRun, runOnce, autoRunDelay = 1500 } = props;
    const [run, setRun] = React.useState(false);
    const tooltipContainer = useTooltipContainer();

    // Add a delay to the tour's start in order to make sure the slide transition/render has completed...
    React.useEffect(() => {
        if (!autoRun) return;
        setTimeout(() => setRun(true), autoRunDelay);
    }, [autoRun, autoRunDelay]);

    const TourComponent = runOnce ? RunOnceTour : Joyride;

    const steps = props?.steps?.map(step => ({
        ...step,
        content: _.isString(step?.content) ? <div dangerouslySetInnerHTML={{ __html: step?.content || "" }} /> : step?.content
    }));

    // Deep-merge only the plain-data props so partial template overrides compose with the defaults
    // without cloning the React elements held in `steps` or `locale` values...
    const translated = translateTourProps({
        ...defaultProps,
        ..._.omit(props, "autoRun", "runOnce", "autoRunDelay"),
        locale: { ...defaultProps.locale, ...props.locale },
        floaterProps: { ...defaultProps.floaterProps, ...props.floaterProps },
        styles: _.merge({}, defaultProps.styles, props.styles),
        steps,
        // Tours ran by default before v3 - preserve that for templates that pass neither `run` nor `autoRun`...
        run: autoRun ? run : (props.run ?? true),
        portalElement: _.isFunction(tooltipContainer.current) ? tooltipContainer.current() : tooltipContainer.current,
    });

    return <TourComponent {...translated} />;
}

CanvasTour.metadata = {
    description: "The `CanvasTour` component can be used to display a guided tour for a canvas.  The tour consists of multiple steps, each highlighting a portion of the canvas and displaying a message explaining the highlighted section.  This component is a wrapper around the `React Joyride` library (v3) - consult [the documentation](https://react-joyride.com/) for all the available options.  Prop names from earlier versions of this component (`disableBeacon`, `showSkipButton`, `spotlightClicks`, `styles.options`, `callback`, etc.) remain fully supported via a built-in compatibility layer, and v3-native props (`options`, `onEvent`) are also accepted.",
    isSelfClosing: true,
    attributes: [
        { name: "runOnce", type: "boolean", description: "Determines whether the tour should run only once - or each time the user navigates to this frame.  If `true`, the `id` field is used to uniquely identify this tour.  Defaults to`false`." },
        { name: "id", type: "string", description: "The unique identifier for this tour.  Only used if `runOnce` is set to `true`.  If populated, the tour will only be shown the first time a user views the canvas on a device.  Note: this value must be unique across all canvases, since the `id` is persisted to local storage in order to keep track of viewed tours." },
        {
            name: "steps",
            type: "object",
            exactType: "{ title: string, content: React.ReactNode, data?: any, target: string, disableBeacon?: boolean, placement?: 'top' | 'bottom' | 'left' | 'right' }[]",
            template: `steps={[\n{ title: "", content: "", target: "", disableBeacon: true }\n]}`,
            description: `The steps for this tour.  Each step will highlight a selected portion of the canvas and display a relevant message.  The user can navigate back and forth between the different steps.  See below for the structure of the \`Step\` object.\n\n

## Step Fields:

| Name | Type | Description |
|------|------|-------------|
| \`title\` | \`string\` | The title to display in the header of the step dialog. |
| \`content\` | \` string \| React.ReactNode\` | The content to display in the body of the step dialog.  Supports JSX, plain text and HTML markup strings. |
| \`data\` | \`any\` | Optional data to be passed to the \`onStepCallback\` function.  This can be used for any display/control logic within the callback function.  Optional - no default. |
| \`target\` | \`string\` | The CSS selector used to determine the element to highlight during this step.  Can use any of the standard CSS selectors - \`Type\`, \`Class\`, \`ID\`, \`Attribute\`, etc.  See [this link](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Selectors) for the complete documentation on CSS Selectors. |
| \`disableBeacon\` | \`boolean\` | In Joyride, a beacon is used to start or continue a tour.  If this property is set to \`true\`, the pop-up for this step is automatically shown when a tour is started or the user closes the previous step.  If not set (or set to \`false\`), the pop-up has to be manually triggered by a user, by clicking on the beacon.  The v3 name \`skipBeacon\` is also accepted. |
| \`placement\` | \`string\` | Optional field that determines where the dialog should be shown in relation to the highlighted element.  Valid options are: \`"top"\`, \`"bottom"\`, \`"left"\` and \`"right"\`. |` },
        { name: "autoRun", type: "boolean", description: "Determines whether the tour should start automatically when the canvas is viewed for the first time.  If `false`, the developer would have to add a button or similar to the canvas that can trigger a manual run." },
        { name: "autoRunDelay", type: "number", description: "The time (in milliseconds) to wait between the `CanvasTour` appearing and the tour starting, if `autoRun` is true.  This is used to provide an opportunity for other components to finish rendering before the tour starts.  Optional - defaults to `1500`." },
    ]
};

export default CanvasTour;
